"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  Suspense,
} from "react";
import { BillingData } from "@/types/auth";
import { fetchBillingData, verifySubscriptionSession, TopupAPI } from "@/lib/api";
import { useRouter } from "next/navigation";
import SearchParamsObserver from "@/components/shared/SearchParamsObserver";
import { toast } from "sonner";
import { FillAppError } from "@/lib/FillAppError";
import { useAuthMinimal } from "@/hooks/useAuthMinimal";
import posthog from "posthog-js";

/**
 * Billing Context State
 */
interface BillingContextState {
  billingData: BillingData | null;
  loading: boolean;
  error: string | null;
}

/**
 * Billing Context Actions
 */
interface BillingContextActions {
  refetchBillingData: () => Promise<void>;
  clearError: () => void;
}

/**
 * Complete billing context type
 */
type BillingContext = BillingContextState & BillingContextActions;

/**
 * Billing Context
 */
const BillingContext = createContext<BillingContext | null>(null);

/**
 * Billing Provider Props
 */
interface BillingProviderProps {
  children: React.ReactNode;
}

/**
 * Billing Provider Component
 */
export function BillingProvider({ children }: BillingProviderProps): React.ReactElement {
  // State
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionVerifying, setSessionVerifying] = useState(false);

  // Auth context

  const { user } = useAuthMinimal();
  
  // Navigation
  const [queryString, setQueryString] = useState("");
  const router = useRouter();

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch billing data
   */
  const refetchBillingData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const data = await fetchBillingData();
      setBillingData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch billing data";
      setError(errorMessage);
      console.error("Billing data fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Handle payment success and cancel URL parameters
   */
  useEffect(() => {
    if (!user) return;
    const searchParams = new URLSearchParams(queryString);
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    const type = searchParams.get('type');

    // TODO: this can go to /pricing page, separate component. no billing related logic.
    const canceled = searchParams.get('canceled');
    if (success === 'true' && sessionId) {
      // Handle payment success
      const verifySession = async () => {
        try {
          setSessionVerifying(true);
          
          if (type === 'topup') {
            // Handle top-up payment
            await TopupAPI.verifyTopupSession(sessionId);
            posthog.capture("topup_payment_successful");
            toast.success("Credits added successfully! Your top-up is now available.");
          } else {
            // Handle subscription payment
            await verifySubscriptionSession(sessionId);
            posthog.capture("subscription_payment_successful");
            toast.success("Payment successful! Your subscription is now active.");
          }
          
          // Refetch billing data to get updated information
          await refetchBillingData();

          const url = new URL(window.location.href);
          url.searchParams.delete('success');
          url.searchParams.delete('session_id');
          url.searchParams.delete('type');
          router.replace(url.pathname + url.search, { scroll: false });

        } catch (error) {
          console.error("Session verification error:", error);
          const eventName = type === 'topup' 
            ? "topup_payment_verification_failed" 
            : "subscription_payment_verification_failed";
          
          posthog.capture(eventName, {
            sessionId,
            error: error instanceof Error ? error.message : String(error),
          });
          
          if (error instanceof FillAppError) {
            toast.error(error.message);
          } else {
            toast.error("Failed to verify payment. Please contact support if you were charged.");
          }
        } finally {
          setSessionVerifying(false);
        }
      };

      verifySession();
    } else if (canceled === 'true') {
      console.log("Payment was canceled. You can try again anytime.");
      // Handle payment cancellation
      toast.error("Payment was canceled. You can try again anytime.");
      const url = new URL(window.location.href);
      url.searchParams.delete('canceled');
      url.searchParams.delete('type');
      router.replace(url.pathname + url.search, { scroll: false });

    }
  }, [queryString, router, refetchBillingData, user]);

  /**
   * Fetch billing data when user changes
   */
  useEffect(() => {
    if (user) {
      refetchBillingData();
    } else {
      setBillingData(null);
      setError(null);
    }
  }, [user, refetchBillingData]);

  /**
   * Context value
   */
  const contextValue: BillingContext = {
    // State
    billingData,
    loading: loading || sessionVerifying,
    error,

    // Actions
    refetchBillingData,
    clearError,
  };

  return (
    <BillingContext.Provider value={contextValue}>
      <Suspense fallback={null}>
        <SearchParamsObserver onChange={setQueryString} />
      </Suspense>
      {children}
    </BillingContext.Provider>
  );
}

/**
 * Hook to use billing context
 */
export function useBilling(): BillingContext {
  const context = useContext(BillingContext);

  if (!context) {
    throw new Error("useBilling must be used within a BillingProvider");
  }

  return context;
}

export default BillingContext;
